/*!
 * Eberlestock Cross-Sell Widget — runtime
 * Rendered by: snippets/cross-sell-widget.liquid (block in main-product)
 * Data file:   assets/cross-sell-overrides.json
 *
 * Uses the theme's cart infrastructure:
 *   - cart-drawer → opens drawer with section rendering
 *   - cart-notification → shows notification with section rendering
 *   - page → redirects to /cart
 * Publishes PUB_SUB_EVENTS.cartUpdate so the rest of the theme stays in sync.
 *
 * ── CHANGE LOG ─────────────────────────────────────────────────────────────
 * [collection-restrict] Added per-anchor `restrict_to_collection` support.
 *   When an anchor's override sets "restrict_to_collection": "<collection-handle>",
 *   the candidate list is filtered to ONLY products in that collection. Used to
 *   keep the Modframe / Mainframe II recommendations in lockstep with the
 *   "Build Your Kit" 15% discount (Kit Companions collection: hunt-emod-kit).
 *   Changes: cache.collections, fetchCollectionHandles(), and a filter at the
 *   end of buildCandidateList(). Search "[collection-restrict]" to find them.
 * [compare-at-bundle] Bundle-qualified totals now mirror the native discount
 *   off COMPARE-AT prices. The native automatic discounts (Hunt EMOD kit
 *   `bundle_discount` 25%, `bundle_discounts.bino` 30% — the only paths where
 *   `qualifies` can be true) bring each qualifying item to pct off its
 *   ORIGINAL price: an item already discounted that deep in the sale gets no
 *   extra, a shallower sale item gets topped up. The widget now computes the
 *   payable total the same way, per item (min(selling, pct off compare-at)),
 *   shows the compare-at total as the strikethrough, and always labels the
 *   savings line with the CONFIGURED percent. Verified against a live cart:
 *   Recon host $139→$97.30 + two $42 pouches→$29.40 = $156.10 vs $223.00
 *   strikethrough, Save 30%. Non-bundle anchors and the price-ratio filter
 *   are unchanged. Search "[compare-at-bundle]" to find it.
 * [savings-dollars] Savings line now reads "Save $X.XX" (actual dollars vs
 *   the compare-at total) instead of "Save N% −$X.XX". Search
 *   "[savings-dollars]" to find it.
 * [addon-extra] Optional per-branch `addon_extra_percentage`. When set, a
 *   qualifying kit shows every item at its current sale price, and every
 *   item NOT in that branch's `hosts` pool gets an extra N% off its sale
 *   price. Mirrors the bundle function app (discount on line group B only).
 *   Branches without the key keep the [compare-at-bundle] behavior.
 *   Search "[addon-extra]" to find it.
 * [addon-extra-floor] The bundle function app rounds each discount amount
 *   DOWN to the cent (e.g. 5% of $36.75 = $1.8375 -> $1.83 off). The preview
 *   now does the same, so it matches checkout to the cent.
 * [non-bundle-pool] Anchors without bundle_slots (packs with no discounted
 *   kit) now only recommend products from overrides.non_bundle_defaults
 *   .restrict_to_collection (the "pack-add-ons" collection), unless the
 *   anchor sets its own restrict_to_collection. Shopify Recs still rank
 *   them; any open slots are filled from the collection in its manual
 *   order. Search "[non-bundle-pool]" to find it.
 * [note-style] "Save $X" is 16px and the note under it is 14px semibold in
 *   the widget text color (px, because this theme sets 1rem to ~10px).
 * ───────────────────────────────────────────────────────────────────────────
 */
(function () {
  "use strict";

  var cache = {
    overridesPromise: null,
    shopifyRecs: new Map(),
    products: new Map(),
    collections: new Map(), // [collection-restrict] handle -> Promise<Set<productHandle>>
  };

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function money(cents, currency) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
      return Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    var v = (cents / 100).toFixed(2);
    return (currency || "USD") === "USD" ? "$" + v : v + " " + (currency || "");
  }

  async function fetchJSON(url) {
    var res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Fetch failed: " + url + " (" + res.status + ")");
    return res.json();
  }

  function loadOverrides(url) {
    if (!cache.overridesPromise) {
      cache.overridesPromise = fetchJSON(url).catch(function (e) {
        console.warn("[es-xsell] overrides missing:", e);
        return { overrides: {}, universal_backup_pool: { handles: [] } };
      });
    }
    return cache.overridesPromise;
  }

  function fetchProduct(handle) {
    if (!cache.products.has(handle)) {
      cache.products.set(handle, fetchJSON("/products/" + handle + ".js"));
    }
    return cache.products.get(handle);
  }

  // [collection-restrict] Fetch the set of product handles in a collection.
  // Uses the storefront /collections/<handle>/products.json endpoint (verified
  // enabled on eberlestock.com). Paginates defensively; caches the result so the
  // collection is fetched at most once per page load. On any failure it returns
  // null, which the caller treats as "no restriction" (fail-open, so a transient
  // network error never blanks the widget).
  function fetchCollectionHandles(collectionHandle) {
    if (!collectionHandle) return Promise.resolve(null);
    if (!cache.collections.has(collectionHandle)) {
      var promise = (async function () {
        var handles = new Set();
        var MAX_PAGES = 10; // safety cap
        for (var page = 1; page <= MAX_PAGES; page++) {
          var url = "/collections/" + collectionHandle + "/products.json?limit=250&page=" + page;
          var res;
          try {
            res = await fetchJSON(url);
          } catch (e) {
            console.warn("[es-xsell] collection fetch failed:", collectionHandle, e);
            return handles.size ? handles : null; // fail-open
          }
          var products = (res && res.products) || [];
          if (!products.length) break;
          products.forEach(function (p) { if (p && p.handle) handles.add(p.handle); });
          if (products.length < 250) break;
        }
        return handles.size ? handles : null;
      })();
      cache.collections.set(collectionHandle, promise);
    }
    return cache.collections.get(collectionHandle);
  }

  function fetchShopifyRecs(anchorProductId, anchorHandle, intent, limit) {
    var cacheKey = anchorHandle + ":" + intent;
    if (cache.shopifyRecs.has(cacheKey)) return cache.shopifyRecs.get(cacheKey);

    if (!anchorProductId) {
      var empty = Promise.resolve([]);
      cache.shopifyRecs.set(cacheKey, empty);
      return empty;
    }

    var url = "/recommendations/products.json?product_id=" + encodeURIComponent(anchorProductId) +
              "&intent=" + encodeURIComponent(intent) + "&limit=" + (limit || 10);

    var promise = fetchJSON(url).then(function (res) {
      var handles = [];
      if (res && Array.isArray(res.products)) {
        for (var i = 0; i < res.products.length; i++) {
          var p = res.products[i];
          if (p && p.handle) {
            handles.push(p.handle);
            if (!cache.products.has(p.handle)) cache.products.set(p.handle, Promise.resolve(p));
          }
        }
      }
      return handles;
    }).catch(function (e) {
      console.warn("[es-xsell] Shopify Recs API (" + intent + ") failed for", anchorHandle, e);
      return [];
    });

    cache.shopifyRecs.set(cacheKey, promise);
    return promise;
  }

  // ---- Selection logic ----

  function pickBestVariant(product, anchorColor, settings) {
    var fallback = settings.color_fallback_order || [];
    var usable = function (v) {
      return v.available && (v.inventory_quantity == null || v.inventory_quantity >= settings.min_units);
    };
    var varColor = function (v) {
      return v.option1 || (v.options && v.options[0]) || null;
    };

    if (settings.use_color_match && anchorColor) {
      var exact = product.variants.find(function (v) {
        return usable(v) && varColor(v) && varColor(v).toLowerCase() === anchorColor.toLowerCase();
      });
      if (exact) return { variant: exact, matchType: "exact" };
    }
    for (var i = 0; i < fallback.length; i++) {
      var v = product.variants.find(function (x) {
        return usable(x) && varColor(x) && varColor(x).toLowerCase() === fallback[i].toLowerCase();
      });
      if (v) return { variant: v, matchType: "fallback" };
    }
    var any = product.variants.find(usable);
    return { variant: any || product.variants[0], matchType: "first-available" };
  }

  function productHasUsableVariant(product, minUnits) {
    return product.variants.some(function (v) {
      return v.available && (v.inventory_quantity == null || v.inventory_quantity >= minUnits);
    });
  }

  function dedupeKeepingOrder(arr) {
    var seen = new Set();
    return arr.filter(function (x) { return !seen.has(x) && seen.add(x); });
  }

  async function buildCandidateList(anchorConfig, overridesConfig, settings) {
    var anchorHandle = anchorConfig.handle;
    var overrides = (overridesConfig.overrides && overridesConfig.overrides[anchorHandle]) || { force_partners: [], blacklist: [] };
    var backups = (overridesConfig.universal_backup_pool && overridesConfig.universal_backup_pool.handles) || [];
    var globalBlacklist = (overridesConfig.global_blacklist && overridesConfig.global_blacklist.handles) || [];
    var blacklist = new Set((overrides.blacklist || []).concat(globalBlacklist));
    var keep = function (h) { return h && h !== anchorHandle && !blacklist.has(h); };

    var forced = (overrides.force_partners || []).filter(keep);

    // Fire both recs intents in parallel
    var recsResults = await Promise.all([
      fetchShopifyRecs(anchorConfig.id, anchorHandle, "complementary", 10),
      fetchShopifyRecs(anchorConfig.id, anchorHandle, "related", 10)
    ]);
    var complementaryHandles = recsResults[0].filter(keep);
    var relatedHandles = recsResults[1].filter(keep);

    var backupHandles = (backups || []).filter(keep);

    var candidates = dedupeKeepingOrder([].concat(forced, complementaryHandles, relatedHandles, backupHandles));

    // [collection-restrict] If this anchor is restricted to a collection, keep
    // ONLY candidates that are members of it. Applies to every source above
    // (force_partners included) so nothing outside the collection can surface.
    // fetchCollectionHandles fails open (returns null) — if the collection can't
    // be read we leave the list unfiltered rather than blank the widget.
    // [non-bundle-pool] Fall back to the global curated pool when the anchor
    // has no restriction of its own.
    var restrictTo = overrides.restrict_to_collection ||
      (overridesConfig.non_bundle_defaults && overridesConfig.non_bundle_defaults.restrict_to_collection) ||
      null;
    if (restrictTo) {
      var allowed = await fetchCollectionHandles(restrictTo);
      if (allowed) {
        candidates = candidates.filter(function (h) { return allowed.has(h); });
        // [non-bundle-pool] Recs-ranked members first, then the rest of the
        // collection in its manual order so the widget never comes up empty.
        candidates = dedupeKeepingOrder(candidates.concat(Array.from(allowed).filter(keep)));
      }
    }

    return candidates;
  }

  async function pickActiveCrossSells(anchorConfig, anchorProduct, anchorVariant, overridesConfig, settings) {
    var handles = await buildCandidateList(anchorConfig, overridesConfig, settings);
    var priceCeilingCents = anchorVariant.price * settings.price_ratio_max;

    // Prefetch all candidate products in parallel
    var productPromises = handles.map(function (h) {
      return fetchProduct(h).catch(function (e) {
        console.warn("[es-xsell] failed to fetch", h, e);
        return null;
      });
    });
    var products = await Promise.all(productPromises);

    var picks = [];
    for (var i = 0; i < products.length; i++) {
      if (picks.length >= settings.max_picks) break;
      var product = products[i];
      if (!product) continue;

      if (!productHasUsableVariant(product, settings.min_units)) continue;
      var minVariantPrice = Math.min.apply(null, product.variants.map(function (v) { return v.price; }));
      if (minVariantPrice > priceCeilingCents) continue;

      picks.push({ product: product });
    }
    return picks;
  }

  // [bundle-15] Slot-based picker for Build Your Kit anchors.
  // Each anchor's override defines bundle_slots: an ordered list of category
  // slots (e.g. { pool: "packs" }, { pool: "accessories" }). For each slot we
  // pick the highest Shopify-Recs-ranked, in-stock product from that pool,
  // falling back to the pool's / slot.prefer's declared order. Price and
  // collection filters do NOT apply here — the pools are the curation. A kit is
  // always frame + one pack + one accessory (a pack = Vapor/Brooks/Batwing,
  // mutually exclusive), which is exactly what earns the 15% discount.
  async function pickSlottedCrossSells(anchorConfig, anchorProduct, overridesConfig, settings, bundleCfg) {
    var overrides = (overridesConfig.overrides && overridesConfig.overrides[anchorConfig.handle]) || {};
    var slots = overrides.bundle_slots || [];
    var pools = (bundleCfg && bundleCfg.pools) || {};

    // Cached recs, used only to rank candidates WITHIN a pool.
    var recsResults = await Promise.all([
      fetchShopifyRecs(anchorConfig.id, anchorConfig.handle, "complementary", 10),
      fetchShopifyRecs(anchorConfig.id, anchorConfig.handle, "related", 10)
    ]);
    var recsOrder = dedupeKeepingOrder([].concat(recsResults[0], recsResults[1]));
    var recRank = new Map();
    recsOrder.forEach(function (h, i) { recRank.set(h, i); });

    var picks = [];
    var used = new Set([anchorConfig.handle]);

    for (var s = 0; s < slots.length; s++) {
      var slot = slots[s] || {};
      var poolList = (slot.prefer && slot.prefer.length ? slot.prefer : (pools[slot.pool] || [])).slice();

      // Order: Recs rank first, then declared pool/prefer order (stable).
      var ordered = poolList
        .map(function (h, idx) { return { h: h, idx: idx }; })
        .sort(function (a, b) {
          var ra = recRank.has(a.h) ? recRank.get(a.h) : Infinity;
          var rb = recRank.has(b.h) ? recRank.get(b.h) : Infinity;
          if (ra !== rb) return ra - rb;
          return a.idx - b.idx;
        })
        .map(function (o) { return o.h; });

      var chosen = null;
      for (var i = 0; i < ordered.length; i++) {
        var h = ordered[i];
        if (used.has(h)) continue;
        var product = await fetchProduct(h).catch(function () { return null; });
        if (!product) continue;
        if (!productHasUsableVariant(product, settings.min_units)) continue;
        chosen = product;
        break;
      }
      if (chosen) {
        used.add(chosen.handle);
        picks.push({ product: chosen });
      }
    }
    return picks;
  }

  // ---- Rendering ----

  function variantImageFor(product, variant) {
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    if (variant.image && variant.image.src) return variant.image.src;
    if (typeof variant.featured_image === "string") return variant.featured_image;
    return product.featured_image || (product.images && product.images[0]) || "";
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  function renderRow(picked, anchorColor, settings, labels, currency) {
    var product = picked.product;
    var result = pickBestVariant(product, anchorColor, settings);
    var variant = result.variant;
    var colorMatched = result.matchType === "exact";
    var pdpUrl = "/products/" + product.handle;
    var target = settings.open_pdp_in_new_tab ? ' target="_blank" rel="noopener"' : "";

    var optionsHtml = product.variants.map(function (v) {
      var usable = v.available && (v.inventory_quantity == null || v.inventory_quantity >= settings.min_units);
      var titleText = v.title + (usable ? "" : " — out of stock");
      var selected = v.id === variant.id ? " selected" : "";
      var disabled = usable ? "" : " disabled";
      return '<option value="' + v.id + '" data-price="' + v.price + '" data-compare-at-price="' + (v.compare_at_price || 0) + '" data-image="' + escapeHtml(variantImageFor(product, v)) + '"' + selected + disabled + '>' + escapeHtml(titleText) + '</option>';
    }).join("");

    var imgSrc = variantImageFor(product, variant);
    var rowClass = "es-xsell__row" + (colorMatched ? " is-color-matched" : "");

    return (
      '<div class="' + rowClass + '" data-es-row data-handle="' + escapeHtml(product.handle) + '" data-variant-id="' + variant.id + '">' +
        '<a class="es-xsell__img-link" href="' + escapeHtml(pdpUrl) + '"' + target + ' aria-label="View ' + escapeHtml(product.title) + '">' +
          '<img class="es-xsell__img" src="' + escapeHtml(imgSrc) + '" alt="' + escapeHtml(product.title) + '" loading="lazy" data-es-row-img>' +
        '</a>' +
        '<div class="es-xsell__info">' +
          '<div class="es-xsell__name-row">' +
            '<h3 class="es-xsell__name"><a href="' + escapeHtml(pdpUrl) + '"' + target + '>' + escapeHtml(product.title) + '</a></h3>' +
          '</div>' +
          '<div class="es-xsell__row-tag">' + escapeHtml(labels.row_tag) + '</div>' +
          (product.variants.length > 1
            ? '<div class="es-xsell__selects">' +
                '<select class="es-xsell__select" data-es-row-select>' + optionsHtml + '</select>' +
              '</div>'
            : '<input type="hidden" data-es-row-select value="' + variant.id + '" data-price="' + variant.price + '" data-compare-at-price="' + (variant.compare_at_price || 0) + '" data-image="' + escapeHtml(variantImageFor(product, variant)) + '">') +
        '</div>' +
        '<div class="es-xsell__checkbox-wrap">' +
          '<input type="checkbox" class="es-xsell__checkbox" data-es-row-checkbox checked aria-label="Include ' + escapeHtml(product.title) + ' in bundle">' +
        '</div>' +
      '</div>'
    );
  }

  function renderAnchorRow(anchorConfig, anchorVariantPriceCents, anchorCompareAtCents, labels, currency) {
    var hasCompare = anchorCompareAtCents && anchorCompareAtCents > anchorVariantPriceCents;
    return (
      '<div class="es-xsell__row" data-es-anchor>' +
        '<div class="es-xsell__img-link" aria-hidden="true">' +
          '<img class="es-xsell__img" src="' + escapeHtml(anchorConfig.featured_image) + '" alt="' + escapeHtml(anchorConfig.title) + '" loading="lazy" data-es-anchor-img>' +
        '</div>' +
        '<div class="es-xsell__info">' +
          '<div class="es-xsell__name-row">' +
            '<h3 class="es-xsell__name">' + escapeHtml(anchorConfig.title) + '</h3>' +
            '<span class="es-xsell__this-item">' + escapeHtml(labels.this_item) + '</span>' +
          '</div>' +
          '<div class="es-xsell__price-wrap">' +
            '<div class="es-xsell__price" data-es-anchor-price>' + money(anchorVariantPriceCents, currency) + '</div>' +
            '<s class="es-xsell__price-compare" data-es-anchor-compare-price' + (hasCompare ? '' : ' hidden') + '>' + (hasCompare ? money(anchorCompareAtCents, currency) : '') + '</s>' +
          '</div>' +
        '</div>' +
        '<div class="es-xsell__checkbox-wrap">' +
          '<input type="checkbox" class="es-xsell__checkbox" checked disabled aria-label="' + escapeHtml(labels.this_item) + '">' +
        '</div>' +
      '</div>'
    );
  }

  // ---- Variant change detection ----

  function debounce(fn, wait) {
    var t;
    return function () { clearTimeout(t); var args = arguments; t = setTimeout(function () { fn.apply(null, args); }, wait); };
  }

  function watchAnchorVariant(callback) {
    var debounced = debounce(callback, 60);

    document.addEventListener("variant:change", debounced);
    document.addEventListener("variantChange", debounced);

    var lastVariantParam = new URL(location.href).searchParams.get("variant");
    setInterval(function () {
      var v = new URL(location.href).searchParams.get("variant");
      if (v !== lastVariantParam) { lastVariantParam = v; debounced(); }
    }, 250);

    $$('form[action*="/cart/add"] input[name="id"]').forEach(function (input) {
      var obs = new MutationObserver(debounced);
      obs.observe(input, { attributes: true, attributeFilter: ["value"] });
      input.addEventListener("change", debounced);
    });
  }

  function getCurrentAnchorVariant(anchorProduct) {
    var urlV = new URL(location.href).searchParams.get("variant");
    if (urlV) {
      var v = anchorProduct.variants.find(function (x) { return String(x.id) === urlV; });
      if (v) return v;
    }
    var formInput = $('form[action*="/cart/add"] input[name="id"]');
    if (formInput && formInput.value) {
      var v2 = anchorProduct.variants.find(function (x) { return String(x.id) === formInput.value; });
      if (v2) return v2;
    }
    return anchorProduct.variants.find(function (v) { return v.available; }) || anchorProduct.variants[0];
  }

  function vColor(v) {
    return v.option1 || (v.options && v.options[0]) || null;
  }

  // ---- Add to cart (theme-aware) ----

  function getCartTarget() {
    return document.querySelector("cart-notification") || document.querySelector("cart-drawer");
  }

  async function addBundleToCart(anchorVariantId, crossSellRows, btnEl, labels, cartType) {
    var items = [{ id: parseInt(anchorVariantId, 10), quantity: 1 }];
    crossSellRows.forEach(function (row) {
      var cb = $("[data-es-row-checkbox]", row);
      var sel = $("[data-es-row-select]", row);
      if (cb && cb.checked && sel) {
        items.push({ id: parseInt(sel.value, 10), quantity: 1 });
      }
    });

    btnEl.disabled = true;
    var labelEl = $("[data-es-cta-label]", btnEl);
    var originalLabel = labelEl ? labelEl.innerHTML : "";
    if (labelEl) labelEl.textContent = labels.cta_adding;

    var cartTarget = getCartTarget();

    try {
      var config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      var body = { items: items };

      if (cartTarget) {
        var sectionsToRender = cartTarget.getSectionsToRender().map(function (s) { return s.id || s.section; });
        body.sections = sectionsToRender;
        body.sections_url = window.location.pathname;
        cartTarget.setActiveElement(document.activeElement);
      }

      var res = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/javascript" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        var err = await res.json().catch(function () { return {}; });
        throw new Error(err.description || ("HTTP " + res.status));
      }

      var response = await res.json();

      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cross-sell-bundle",
          productVariantId: String(anchorVariantId),
          cartData: response,
        });
      }

      if (!cartTarget) {
        window.location = window.routes.cart_url;
        return;
      }

      cartTarget.renderContents(response);

      btnEl.classList.add("is-success");
      if (labelEl) labelEl.textContent = labels.cta_success;
      setTimeout(function () {
        btnEl.classList.remove("is-success");
        if (labelEl) labelEl.innerHTML = originalLabel;
        btnEl.disabled = false;
      }, 2200);

    } catch (e) {
      console.error("[es-xsell] add-to-cart failed", e);
      btnEl.disabled = false;
      if (labelEl) labelEl.innerHTML = originalLabel;
      var errEl = $("[data-es-error]", btnEl.closest(".es-xsell"));
      if (errEl) {
        errEl.hidden = false;
        errEl.textContent = "Couldn't add bundle to cart: " + (e.message || "unknown error");
        setTimeout(function () { errEl.hidden = true; }, 4000);
      }
    }
  }

  // ---- Block initializer ----

  async function initBlock(el) {
    var config;
    try { config = JSON.parse(el.dataset.config); }
    catch (e) { console.error("[es-xsell] bad config", e); return; }

    var anchor = config.anchor;
    var settings = config.settings;
    var data_urls = config.data_urls;
    var labels = config.labels;
    var cartType = config.cart_type || "page";
    var currency = anchor.currency || "USD";

    // Fire all initial fetches in parallel:
    // overrides JSON, anchor product, complementary recs, related recs
    var overrides, anchorProduct;
    try {
      var results = await Promise.all([
        loadOverrides(data_urls.overrides),
        fetchProduct(anchor.handle),
        fetchShopifyRecs(anchor.id, anchor.handle, "complementary", 10),
        fetchShopifyRecs(anchor.id, anchor.handle, "related", 10)
      ]);
      overrides = results[0];
      anchorProduct = results[1];
    } catch (e) {
      console.error("[es-xsell] data load failed", e);
      el.classList.add("is-empty");
      return;
    }

    var currentAnchorVariant = getCurrentAnchorVariant(anchorProduct);
    var currentPicks = [];

    // [bundle-15] Bundle-discount config + qualification data.
    // Mirrors the native Shopify discount: min_items+ products from the
    // collection in cart = 15% off. Hosts must be in the collection for a
    // host+2-companions kit to count as 3.
    // Supports multiple branches: an anchor override with "bundle":"<key>" uses
    // overrides.bundle_discounts[key]; anchors without it fall back to the
    // legacy single overrides.bundle_discount (the Hunt EMOD kit).
    var anchorOverride = (overrides.overrides && overrides.overrides[anchor.handle]) || {};
    var bundleCfg =
      (anchorOverride.bundle && overrides.bundle_discounts && overrides.bundle_discounts[anchorOverride.bundle]) ||
      overrides.bundle_discount ||
      null;
    var useSlots = !!(anchorOverride.bundle_slots && anchorOverride.bundle_slots.length);
    var discountSet = null;
    if (bundleCfg && bundleCfg.collection) {
      discountSet = await fetchCollectionHandles(bundleCfg.collection);
    }

    async function rerender() {
      currentAnchorVariant = getCurrentAnchorVariant(anchorProduct);
      currentPicks = useSlots
        ? await pickSlottedCrossSells(anchor, anchorProduct, overrides, settings, bundleCfg)
        : await pickActiveCrossSells(anchor, anchorProduct, currentAnchorVariant, overrides, settings);

      if (!currentPicks.length) {
        el.classList.add("is-empty");
        return;
      }
      el.classList.remove("is-empty");

      var anchorColor = vColor(currentAnchorVariant);
      var anchorImg = variantImageFor(anchorProduct, currentAnchorVariant) || anchorProduct.featured_image || anchor.featured_image;
      var rowsHtml =
        renderAnchorRow(
          { title: anchor.title, featured_image: anchorImg },
          currentAnchorVariant.price,
          currentAnchorVariant.compare_at_price,
          labels,
          currency
        ) +
        currentPicks.map(function (p) { return renderRow(p, anchorColor, settings, labels, currency); }).join("");

      var rowsContainer = $("[data-es-rows]", el);
      rowsContainer.innerHTML = rowsHtml;

      wireRowEvents();
      updateTotals();
    }

    function getSelectedPrice(sel) {
      if (sel.options) return parseInt(sel.options[sel.selectedIndex].dataset.price, 10) || 0;
      return parseInt(sel.dataset.price, 10) || 0;
    }

    function getSelectedCompareAtPrice(sel) {
      var raw = sel.options
        ? sel.options[sel.selectedIndex].dataset.compareAtPrice
        : sel.dataset.compareAtPrice;
      return parseInt(raw, 10) || 0;
    }

    // [bundle-15] Lazily create the savings line as its OWN row directly below
    // the totals block (not a flex sibling inside it, which squishes it next to
    // the price). Full-width, right-aligned via CSS.
    function ensureSavingsEl() {
      var totals = $(".es-xsell__totals", el);
      if (!totals) return null;
      var s = $("[data-es-savings]", el);
      if (!s) {
        s = document.createElement("div");
        s.className = "es-xsell__savings";
        s.setAttribute("data-es-savings", "");
        // Styles are inline so this needs NO external CSS (keeps it from
        // colliding with the theme's widget stylesheet). Starts hidden via
        // display:none; updateTotals flips display to show/hide it.
        s.style.cssText = "display:none;flex-direction:column;align-items:flex-end;gap:2px;margin:-14px 0 10px;padding-right:8px;text-align:right;";
        totals.insertAdjacentElement("afterend", s);
      }
      return s;
    }

    function updateTotals() {
      var total = currentAnchorVariant.price;
      var compareTotal = currentAnchorVariant.compare_at_price || currentAnchorVariant.price;
      var count = 1;
      var selectedHandles = [anchorProduct.handle]; // anchor always included
      // [compare-at-bundle] Per-item prices, kept so the qualifying branch can
      // mirror the native discount item by item.
      var selectedItems = [{
        handle: anchorProduct.handle,
        price: currentAnchorVariant.price,
        compare: currentAnchorVariant.compare_at_price || 0
      }];

      $$("[data-es-row]:not([data-es-anchor])", el).forEach(function (row) {
        var cb = $("[data-es-row-checkbox]", row);
        var sel = $("[data-es-row-select]", row);
        if (cb && cb.checked && sel) {
          var itemPrice = getSelectedPrice(sel);
          var itemCompare = getSelectedCompareAtPrice(sel);
          total += itemPrice;
          compareTotal += (itemCompare > itemPrice ? itemCompare : itemPrice);
          selectedItems.push({ handle: row.dataset.handle, price: itemPrice, compare: itemCompare });
          count += 1;
          if (row.dataset.handle) selectedHandles.push(row.dataset.handle);
        }
      });

      $("[data-es-cta-count]", el).textContent = count;

      var totalEl = $("[data-es-total]", el);
      var compareTotalEl = $("[data-es-compare-total]", el);
      var savingsEl = ensureSavingsEl();

      // [bundle-15] Does the SELECTED set qualify for the automatic 15%?
      // Rule mirrors the native Shopify discount: min_items+ items from the
      // discount collection in cart. If the collection couldn't be read
      // (discountSet null) we fail closed and show no discount, so we never
      // promise a discount that won't apply.
      var pct = bundleCfg ? Number(bundleCfg.percentage || 0) : 0;
      var minItems = bundleCfg ? Number(bundleCfg.min_items || 3) : 3;
      var qualifyingCount = selectedHandles.filter(function (h) {
        return discountSet && discountSet.has(h);
      }).length;
      // [addon-extra] Branches using addon_extra_percentage qualify on count alone.
      var hasAddonExtra = !!(bundleCfg && bundleCfg.addon_extra_percentage != null);
      var addonExtra = hasAddonExtra ? Number(bundleCfg.addon_extra_percentage) || 0 : 0;
      var hostSet = new Set((bundleCfg && bundleCfg.pools && bundleCfg.pools.hosts) || []);
      var qualifies = (pct > 0 || addonExtra > 0) && qualifyingCount >= minItems;

      if (qualifies) {
        // [compare-at-bundle] The native bundle discount is configured off the
        // ORIGINAL (compare-at) price, per item: each qualifying item ends at
        // pct off its compare-at. An item already discounted that deep in the
        // sale gets no extra (the discount never raises a price); an item on a
        // shallower sale gets topped up. Mirror it item by item:
        //   final = min(selling price, pct off compare-at)
        // so the preview matches the cart subtotal to the cent. Items with no
        // compare-at fall back to pct off their selling price.
        var discounted = 0;
        selectedItems.forEach(function (it) {
          if (hasAddonExtra) {
            // [addon-extra] Hosts stay at their sale price; add-ons get an
            // extra addonExtra% off their sale price.
            // [addon-extra-floor] Discount amount rounded DOWN to the cent,
            // matching the function app.
            discounted += hostSet.has(it.handle)
              ? it.price
              : it.price - Math.floor(it.price * addonExtra / 100 + 1e-9);
            return;
          }
          var ref = it.compare > it.price ? it.compare : it.price;
          var target = Math.round(ref * (1 - pct / 100));
          discounted += Math.min(it.price, target);
        });
        var referenceTotal = Math.max(compareTotal, total);
        var savingsAmount = referenceTotal - discounted;
        // [savings-dollars] Label shows the actual dollar savings only (no
        // percent), since items carry different sale depths and a single
        // percent would misstate the kit.
        if (totalEl) totalEl.textContent = money(discounted, currency);
        if (compareTotalEl) {
          compareTotalEl.textContent = money(referenceTotal, currency);
          compareTotalEl.hidden = false;
        }
        if (savingsEl) {
          var note = (bundleCfg && bundleCfg.savings_note) || "Discount applied automatically";
          // Inline styles use the widget's own CSS variables (--es-orange /
          // --es-muted from .es-xsell) so it matches the theme with no extra CSS.
          savingsEl.innerHTML =
            '<span style="color:var(--es-orange,#e85d04);font-weight:700;font-size:16px;letter-spacing:0.04em;text-transform:uppercase;">Save ' + money(savingsAmount, currency) + '</span>' +
            '<span style="color:inherit;font-size:14px;font-weight:600;line-height:1.35;">' + escapeHtml(note) + '</span>';
          savingsEl.style.display = "flex";
        }
      } else {
        if (totalEl) totalEl.textContent = money(total, currency);
        if (compareTotalEl) {
          if (compareTotal > total) {
            compareTotalEl.textContent = money(compareTotal, currency);
            compareTotalEl.hidden = false;
          } else {
            compareTotalEl.hidden = true;
          }
        }
        if (savingsEl) savingsEl.style.display = "none";
      }
    }

    function wireRowEvents() {
      $$("[data-es-row]:not([data-es-anchor])", el).forEach(function (row) {
        var sel = $("[data-es-row-select]", row);
        var cb  = $("[data-es-row-checkbox]", row);
        if (sel && sel.tagName === "SELECT") {
          sel.addEventListener("change", function () {
            var opt = sel.options[sel.selectedIndex];
            var img = $("[data-es-row-img]", row);
            if (img && opt.dataset.image) img.src = opt.dataset.image;
            updateTotals();
          });
        }
        if (cb) cb.addEventListener("change", updateTotals);
      });
    }

    var cta = $("[data-es-add-bundle]", el);
    if (cta) {
      cta.addEventListener("click", function () {
        addBundleToCart(currentAnchorVariant.id, $$("[data-es-row]:not([data-es-anchor])", el), cta, labels, cartType);
      });
    }

    function updateAnchorDisplay() {
      currentAnchorVariant = getCurrentAnchorVariant(anchorProduct);
      var anchorImg = $("[data-es-anchor-img]", el);
      if (anchorImg) {
        var newSrc = variantImageFor(anchorProduct, currentAnchorVariant);
        if (newSrc && anchorImg.src !== newSrc) anchorImg.src = newSrc;
      }
      var anchorPrice = $("[data-es-anchor-price]", el);
      if (anchorPrice) anchorPrice.textContent = money(currentAnchorVariant.price, currency);
      var anchorCompare = $("[data-es-anchor-compare-price]", el);
      if (anchorCompare) {
        var cap = currentAnchorVariant.compare_at_price;
        var hasCompare = cap && cap > currentAnchorVariant.price;
        anchorCompare.textContent = hasCompare ? money(cap, currency) : '';
        anchorCompare.hidden = !hasCompare;
      }
      updateTotals();
    }

    watchAnchorVariant(async function () {
      var newV = getCurrentAnchorVariant(anchorProduct);
      if (newV.id !== currentAnchorVariant.id) {
        updateAnchorDisplay();
        await rerender();
      }
    });

    await rerender();
  }

  function init() {
    $$("[data-es-xsell]").forEach(initBlock);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:block:select", function (e) {
      var block = e.target.querySelector("[data-es-xsell]");
      if (block) initBlock(block);
    });
  }
})();